import { ArrowRightStartOnRectangleIcon, ArrowUturnLeftIcon, Bars3BottomLeftIcon, KeyIcon, RectangleStackIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import UserImage from "src/assets/images/user.jpg";
import { apiRequest } from "src/async/apiUtils";
import { logoutRedirect } from "src/async/fetch";
import Image from "src/components/elements/Image";
import DropdownWrapper from "src/components/form/DropdownWrapper";
import Input from "src/components/form/Input";
import Modal from "src/components/shared/Modal";
import UserDropdown from "src/components/shared/UserDropdown";
import { AuthContext } from "src/context/auth";
import { WorkspaceContext } from "src/context/workspace";
import { classNames } from "src/helpers/classNames";
import ResetPasswordForm from "src/pages/forgot-password/ResetPasswordForm";
import ManagePayments from "src/pages/ManagePayments";
import CustomModal from "src/components/shared/CustomModal";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import SlideOver from "./SlideOver";
import { isA2HSDismissedForWS, setA2HSDismissedForWS } from "src/helpers";
import AddToHomeScreen from "../AddToHomeScreen";
import HeaderMenus from "../navigation/HeaderMenus";
import { useSessionResumeTracker } from "src/hooks/useSessionResumeTracker";
import { getUserMenuData, isPasswordInvalid } from "src/helpers/userHelper";
import { useAsyncAction } from "src/hooks/useAsyncAction";
import { useWorkspaceManager } from "src/hooks/useWorkspaceManager";
import { Settings } from "./Settings/Settings";
import useActivePage from "src/hooks/useActivePage";
import { useHeaderMenus } from "src/hooks/useHeaderMenus";
import { useSiteSettings } from "src/context/SiteSettingsContext";
import useDevice from "src/hooks/useDevice";

const Header = ({ isMenu, setIsMenu, menuType, menus = [], mordernDesign }) => {
  const { workspace_id, content_value } = useParams();
  const { group: group_id, page: page_id } = useUrlQueryParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const fromTravel = new URLSearchParams(search).get("from") === "travel";

  const handleBackToTravel = () => {
    const params = new URLSearchParams(search);
    params.delete("page");
    params.delete("from");
    params.delete("profile");
    const qs = params.toString();
    navigate({ search: qs ? `?${qs}` : "" }, { replace: true });
  };
  const { site } = useSiteSettings();
  const device = useDevice();
  const { user } = useContext(AuthContext);
  const { loading, currentLogo, switchAdminButtonDisabled, clickWorkspace, loadPlanDetails, navigateToPaymemnts, extractWorkspaceName, loadImage, onHandleSwitchToAdmin, navigateToSettings, settingsSlider, setSettingsSlider } = useWorkspaceManager();
  const { list: workspaces, details: workspaceDetails, workspacePaymentModalOpen, setWorspacePaymentModalOpen = () => {}, planDetails, params, setParams } = useContext(WorkspaceContext);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [userMenu, setUserMenu] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [overflowedHeaderMenus, setOverflowedHeaderMenus] = useState([]);
  const [headerHeight, setHeaderHeight] = useState(0);

  const payment_required = window.location.href.includes("/payment-required");
  const isExtension = window.location.protocol === "chrome-extension:";

  useSessionResumeTracker({ workspace_id, group_id, page_id });

  const passwordValidation = !password || !confirmPassword || !oldPassword || password !== confirmPassword || isPasswordInvalid(password, site.password_policy);

  const logout = () => {
    logoutRedirect();
  };

  const {
    loading: changePasswordLoading,
    success: changePasswordSuccess,
    execute: changePasswordAction,
  } = useAsyncAction({
    onSuccess: (res) => {
      const resData = res.data;
      if (resData.status === 200) {
        setIsOpenModal(false);

        clearChangePasswordModalValues();
      } else {
        toast.error(resData.message);
        clearChangePasswordModalValues();
      }
    },
    onError: (error) => {
      toast.error(error.message);
      clearChangePasswordModalValues();
    },
  });

  const { menus: headerMenus } = useHeaderMenus({
    workspaceId: workspace_id,
    userId: user?._id,
    paymentRequired: payment_required,
  });

  useEffect(() => {
    if (device === "mobile" && site?.header_menu_height > 0) {
      setHeaderHeight(site.header_menu_height);
    } else {
      setHeaderHeight(0);
    }
  }, [device, site?.header_menu_height]);

  useEffect(() => {
    if (workspace_id && user?._id) {
      loadPlanDetails(workspace_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace_id, user?._id]);

  useEffect(() => {
    if (planDetails?.active_plan?.plan_details?.pricing?.type !== "no_charge" && planDetails?.active_plan?.payment_required) {
      setWorspacePaymentModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planDetails?.active_plan]);

  useEffect(() => {
    const dropdownData = getUserMenuData({
      user,
      workspaceDetails,
      overflowedHeaderMenus,
      setIsOpenModal,
      logout,
      UserImage,
      KeyIcon,
      ArrowRightStartOnRectangleIcon,
      onHandleSwitchToAdmin,
      navigateToPaymemnts,
      navigateToSettings,
      switchAdminButtonDisabled,
      navigateToTravel,
      site,
    });

    setUserMenu({
      image: UserImage,
      name: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Administrator",
      dropdownData,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, user?.type, workspaceDetails?.notification_banner_enable, workspaceDetails?.billing_elegible_user_id, overflowedHeaderMenus, switchAdminButtonDisabled, site?.travel_enabled]);

  const navigateToTravel = () => {
    navigate("/travel/dashboard");
  };

  const clearChangePasswordModalValues = () => {
    setOldPassword("");
    setPassword("");
    setConfirmPassword("");
  };

  useEffect(() => {
    if (!isOpenModal) {
      clearChangePasswordModalValues();
    }
  }, [isOpenModal]);

  const changePassword = async () => {
    changePasswordAction(async () => {
      return await apiRequest("post", "/portal/user/self/change-password", {
        body: {
          old_password: oldPassword,
          password,
          confirm_password: confirmPassword,
        },
      });
    });
  };

  useEffect(() => {
    loadImage();
  }, [workspaceDetails, site, loadImage]);

  useEffect(() => {
    if (workspace_id && window.innerWidth < 480) {
      setOpenDrawer(!isA2HSDismissedForWS(workspace_id));
    }
  }, [workspace_id]);

  const handleClose = () => {
    setA2HSDismissedForWS(workspace_id);
    setOpenDrawer(false);
  };

  const page_type = useActivePage({
    menus: headerMenus,
    page_id: content_value,
    document_id: content_value,
    form_id: content_value,
  });

  useEffect(() => {
    setParams({ ...params, page_type });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page_type]);

  return (
    <>
      <div
        className={classNames("sticky left-0 top-0 z-40 flex w-full flex-shrink-0 items-center bg-headerColor transition-all xl:z-50", workspaceDetails.workspace_type !== "JWT_FULL_EMBED" && !workspacePaymentModalOpen ? "shadow" : "", mordernDesign ? "!bg-leftNavColor" : "")}
        style={{ height: headerHeight > 0 ? headerHeight : "4rem" }}>
        <div className={classNames("relative flex h-full w-full items-center justify-between pr-6")}>
          <div className={classNames("animate-fade-in flex items-center ")}>
            {!(!workspaceDetails?._id || !menus?.length || menus?.length === 1) && !payment_required && (
              <button
                onClick={() => setIsMenu(!isMenu)}
                className={classNames("group group ml-2 flex h-9 w-9 items-center justify-center rounded-md transition-all", menuType === "vertical" ? "" : "xl:hidden")}>
                <Bars3BottomLeftIcon className="h-7 w-7 text-headerTextColor transition-all group-hover:stroke-2" />
              </button>
            )}

            {menus?.length <= 1 && content_value && (
              <motion.button
                key="slide-button"
                initial={{ x: -20, opacity: 0, scale: 0.9 }}
                animate={{ x: 0, opacity: 1, scale: 1 }}
                exit={{ x: -20, opacity: 0, scale: 0.9 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 30,
                  mass: 0.8,
                }}
                onClick={() => navigate(`/workspace/${workspace_id}/embed`)}
                data-tooltip-id="back-to-dashboard-tip"
                data-tooltip-content="Back to dashboard"
                className={classNames("group relative ml-3 flex h-10 w-10 items-center justify-center rounded-xl", "border border-white/20 bg-white/10 shadow-sm backdrop-blur-md", "transition-all duration-200 hover:border-white/40 hover:bg-white/20 hover:shadow-md")}>
                <div className="bg-headerTextColor/5 absolute inset-0 rounded-xl opacity-0 transition-opacity group-hover:opacity-100" />
                <ArrowUturnLeftIcon className={classNames("h-5 w-5 text-headerTextColor transition-transform duration-200 group-hover:-translate-x-0.5", mordernDesign ? "!text-leftNavTextColor" : "")} />
              </motion.button>
            )}

            {fromTravel && (
              <motion.button
                key="slide-button"
                initial={{ x: -20, opacity: 0, scale: 0.9 }}
                animate={{ x: 0, opacity: 1, scale: 1 }}
                exit={{ x: -20, opacity: 0, scale: 0.9 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 30,
                  mass: 0.8,
                }}
                onClick={handleBackToTravel}
                data-tooltip-id="back-to-dashboard-tip"
                data-tooltip-content="Back to dashboard"
                className={classNames("group relative ml-3 flex h-10 w-10 items-center justify-center rounded-xl", "border border-white/20 bg-white/10 shadow-sm backdrop-blur-md", "transition-all duration-200 hover:border-white/40 hover:bg-white/20 hover:shadow-md")}>
                <div className="bg-headerTextColor/5 absolute inset-0 rounded-xl opacity-0 transition-opacity group-hover:opacity-100" />
                <ArrowUturnLeftIcon className={classNames("h-5 w-5 text-headerTextColor transition-transform duration-200 group-hover:-translate-x-0.5", mordernDesign ? "!text-leftNavTextColor" : "")} />
              </motion.button>
            )}

            <Tooltip
              id="back-to-dashboard-tip"
              place="bottom"
              delayShow={250}
              className="!rounded-md !px-3 !py-2 !text-xs !shadow-lg"
              style={{ background: "rgba(0,0,0,0.9)", color: "white", zIndex: 9999 }}
            />

            {(workspace_id || window.location.pathname === "/workspace-paused") && workspaceDetails?._id && workspaces.length > 1 ? (
              <div className={classNames(menuType === "vertical" && menus?.length === 1 && "pl-2", menus?.length > 1 && menuType === "horizontal" && "pl-2")}>
                <DropdownWrapper
                  key={workspaces?.length}
                  data={workspaces.map((workspace) => ({
                    ...workspace,
                    image: workspace.square_logo || workspace.image_logo || workspace.image_favicon,
                  }))}
                  onClick={clickWorkspace}
                  currentId={extractWorkspaceName()}
                  mordernDesign={mordernDesign}
                  content={
                    <>
                      {currentLogo ? (
                        <div
                          className={classNames("mb-[1px] flex py-[2px]", headerHeight === 0 && "h-12  sm:h-14")}
                          style={{ height: headerHeight > 0 ? headerHeight * 0.8 : null }}>
                          <Image
                            src={currentLogo}
                            alt={"logo"}
                            width="100%"
                            height="100%"
                            classes="object-contain object-left max-h-full max-w-full !h-auto !w-auto"
                            hideImageOnError
                          />
                        </div>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center rounded border border-headerColor bg-headerColor px-3 py-2 font-medium text-headerTextColor transition-all hover:border-headerTextColor hover:shadow">
                          Switch workspace
                          <RectangleStackIcon className="ml-[8px] h-5 w-5" />
                        </div>
                      )}
                    </>
                  }
                  buttonLabel="Workspaces"
                  buttonClass="w-full max-w-sm"
                  buttonIcon={true}
                  dropdownContainer="ml-11"
                />
              </div>
            ) : (
              <div className="max-w-sm pr-8">
                <div
                  className={classNames("mb-[1px] flex py-[2px]", headerHeight === 0 && "h-12  sm:h-14")}
                  style={{ height: headerHeight > 0 ? headerHeight * 0.8 : null }}>
                  <Image
                    src={currentLogo}
                    alt={"logo"}
                    width="100%"
                    height="100%"
                    effect="blur"
                    classes="object-contain object-left"
                    hideImageOnError
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!window.location.href.includes("/payment-required") && (
              <HeaderMenus
                menus={headerMenus}
                setOverflowedHeaderMenus={setOverflowedHeaderMenus}
                mordernDesign={mordernDesign}
              />
            )}
            {user?._id && (
              <div className="animate-fade-in ml-auto ">
                <UserDropdown
                  data={userMenu}
                  showName={!workspaceDetails?.notification_banner_enable}
                  xPlacement={"right"}
                  nameClasses={mordernDesign ? "!text-leftNavTextColor" : ""}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        title="Change Password"
        isOpen={isOpenModal}
        onSuccess={changePassword}
        onCancel={() => setIsOpenModal(false)}
        defaultOptions={{
          onSuccessLoaderVisible: changePasswordLoading,
          onSuccessLoaderStart: changePasswordLoading,
          successButtonDisabled: changePasswordLoading || passwordValidation,
          onSuccessButtonSuccess: changePasswordSuccess,
        }}>
        <div className="mb-3">
          <Input
            inline={true}
            inputValue={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            label={"Current Password *"}
            inputType={"password"}
            inputName={"password"}
          />
        </div>
        <hr className="my-5" />
        <ResetPasswordForm
          password={password}
          confirmPassword={confirmPassword}
          setPassword={setPassword}
          setConfirmPassword={setConfirmPassword}
          policy={site?.password_policy}
        />
      </Modal>
      <SlideOver
        size="5xl"
        title="Settings"
        description="Manage your settigs and configurations."
        isOpen={settingsSlider}
        handleClose={() => setSettingsSlider(false)}>
        <Settings />
      </SlideOver>
      {!loading && !window.location.href.includes("/workspace-paused") && (
        <CustomModal
          size="xxl"
          title={planDetails?.active_plan ? (planDetails?.active_plan?.payment_required ? "Plan purchase" : "Manage Payments") : ""}
          hideImage={false || (planDetails && !planDetails?.active_plan?.payment_required)}
          secondaryTitle={workspaceDetails?.name || ""}
          hideCrossIcon={!planDetails?.active_plan || planDetails?.active_plan?.payment_required}
          onClose={() => setWorspacePaymentModalOpen(false)}
          isOpen={workspacePaymentModalOpen}>
          {isExtension ? (
            <div className="flex w-full flex-col items-center justify-center px-2 py-12 sm:px-6 lg:px-8">
              <div className="flex flex-col items-center space-y-6 sm:mx-auto sm:w-full sm:max-w-xl">
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 sm:text-3xl">This workspace is under a subscription</h2>
                <div className="text-regular mt-1 text-center text-gray-600">Please contact your administrator for more details. </div>
              </div>
            </div>
          ) : (
            <ManagePayments
              loading={loading}
              planDetails={planDetails}
              loadPlanDetails={loadPlanDetails}
            />
          )}
        </CustomModal>
      )}
      {!isExtension && (
        <SlideOver
          isOpen={openDrawer}
          handleClose={handleClose}
          hideHeader={true}
          onOpenChange={setOpenDrawer}
          position={"bottom"}>
          <AddToHomeScreen
            site={site}
            onClose={handleClose}
            workspaceDetails={workspaceDetails}
          />
        </SlideOver>
      )}
    </>
  );
};

export default Header;
